import { type ReactNode, useId } from 'react';
import { VisuallyHidden } from '@backstage/ui';
import clsx from 'clsx';

import type { SvgIconComponent } from '../../icons/mui-re-exports';

import styles from './field-row.module.css';

export interface FieldRowProps {
  /** The value rendered next to the label. May be interactive (links). */
  children: ReactNode;
  /** Optional extra class merged onto the row container. */
  className?: string;
  /** Decorative leading icon. Rendered aria-hidden; carries no meaning. */
  icon: SvgIconComponent;
  /** Field label. Becomes the accessible name of the value, not a tooltip. */
  label: string;
}

/**
 * A labelled field row: a decorative leading icon, followed by a value whose
 * accessible name is the field label.
 *
 * Why aria-labelledby instead of a tooltip: a hover tooltip on a
 * non-focusable icon is not conformant (WCAG 1.4.13 requires the extra
 * content to be reachable and dismissible, which a bare icon cannot offer,
 * and keyboard/touch users never trigger hover at all). The label would also
 * be the icon's only accessible name, so a decorative icon plus a tooltip
 * leaves the value itself unlabelled. Instead the icon is marked decorative
 * (aria-hidden) and the label is rendered visually hidden but linked to the
 * value via aria-labelledby, so assistive tech announces "label: value".
 */
export const FieldRow = (props: FieldRowProps) => {
  const { children, className, icon: Icon, label } = props;
  const labelId = useId();

  return (
    <div className={clsx(styles.row, className)}>
      <span className={styles.icon}>
        <Icon aria-hidden fontSize="small" />
      </span>
      <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
      <span aria-labelledby={labelId}>{children}</span>
    </div>
  );
};
